import * as React from 'react'

import TruncatedDescription from '../truncated_description'
import { StateStandardPerformanceData, ClassroomInterface, SkillInterface, } from '../interfaces';
import {
  DataTable,
  Spinner,
  helpIcon,
  Tooltip,
  DataTableChip,
  ShrinkWrapChip,
} from '../../../../../Shared/index';
import useScaledTableColumnWidths from '../../../../../Shared/hooks/useScaledTableColumnWidths';
import { useStickyTableHeader } from '../../../../../Shared/hooks/useStickyTableHeader';
import { NONE, smartGroupToClassName, getSortableSmartGroup, formatTime, formatMomentumScore, formatTrend, formatPercentage, formatLastPracticed, getSortableMomentumScore, getSortableTrend, getSortablePercentage, getSortableTime, getSortableLastPracticed, getSortableExposure, getSmartGroup } from '../../shared/shared';
import { NoStudentsEmptyState, classroomHasNoStudents, formatGradeLevelWithContext, } from '../shared';
import { useAllSkillCategoriesAndSubCategoriesContext, } from '../all_skill_categories_and_sub_categories'

const helpIconImg = <img alt={helpIcon.alt} src={helpIcon.src} />

interface ByStandardProps {
  classroom: ClassroomInterface;
  containerRef: React.RefObject<HTMLElement>;
  momentumScoreGoal: number;
  data: StateStandardPerformanceData[] | null;
  studentId?: number | string;
  selectedGradeLevel?: string;
}

const ByStandard: React.FC<ByStandardProps> = ({ 
  containerRef,
  classroom,
  data,
  momentumScoreGoal, 
  selectedGradeLevel,
  studentId,
}) => {
  const { allSkillCategories, allSkillSubCategories, } = useAllSkillCategoriesAndSubCategoriesContext()
  
  const tableRef = React.useRef<HTMLTableElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  const columnWidths = [
    { width: 300, minWidth: 180 }, // Standard
    { width: 200, minWidth: 120 }, // Skill Category
    { width: 200, minWidth: 120 }, // Smart Group
    { width: 200, minWidth: 110 }, // Avg Momentum Score
    { width: 200, minWidth: 120 }, // Avg Momentum Score Trend
    { width: 175, minWidth: 100 }, // Avg Exposure
    { width: 175, minWidth: 100 }, // Avg Questions Answered
    { width: 175, minWidth: 100 }, // Avg Replay Rate
    { width: 175, minWidth: 100 }, // Avg Time Spent
    { width: 200, minWidth: 110 }, // Number of Students Practiced / Last Practiced
  ];

  const scaledWidths = useScaledTableColumnWidths({
    columnWidths,
    containerRef,
    maxTableWidth: 2000,
    minTableWidth: 1200,
    additionalWidthRestrictions: 99
  });

  useStickyTableHeader({
    wrapperRef,
    tableRef,
    enabled: true,
    dependencies: [data],
  });


  const formatStudentCount = (numberOfStudents: number, totalClassroomStudents: number) => {
    if (!numberOfStudents || !totalClassroomStudents) return '—';
    const percentage = Math.round((100.0 * numberOfStudents) / totalClassroomStudents);
    return `${numberOfStudents} (${percentage}%)`;
  };

  const isStudentSpecific = Boolean(studentId)

  const headers = [
    {
      width: scaledWidths['300w-180min'],
      ariaLabel: 'Standard',
      name: 'Standard',
      attribute: 'name',
      sortAttribute: 'nameForSort',
      isSortable: true,
      noTooltip: true
    },
    {
      width: scaledWidths['200w-120min'],
      ariaLabel: 'Skill Category',
      name: 'Skill Category',
      attribute: 'skillCategories',
      sortAttribute: 'skillCategoriesForSort',
      isSortable: true,
      noTooltip: true
    },
    {
      width: scaledWidths['200w-120min'],
      ariaLabel: 'Smart Group',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Smart Group</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `Dynamic groups based on how this student's performance for this standard compares against your goal of ${momentumScoreGoal}. These groups also consider the student's exposure to ensure the grouping is reliable. You can change this goal at any time by clicking the 'Change goal' button.`
                : `Dynamic groups based on how the class' average performance for this standard compares against your goal of ${momentumScoreGoal}. These groups also consider the average exposure to ensure the classification is reliable. You can change this goal at any time by clicking the 'Change goal' button.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'smartGroup',
      sortAttribute: 'smartGroupForSort',
      isSortable: true
    },
    {
      width: scaledWidths['200w-110min'],
      ariaLabel: isStudentSpecific ? 'Momentum Score' : 'Avg Momentum Score',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Momentum Score' : 'Avg Momentum Score'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `A growth-focused score that reflects this student's recent improvements and consistent effort for this standard.`
                : `The average Momentum Score across all students for this standard.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgMomentumScore',
      sortAttribute: 'avgMomentumScoreForSort',
      isSortable: true,
      rowSectionClassName: 'momentum-score'
    },
    {
      width: scaledWidths['200w-120min'],
      ariaLabel: isStudentSpecific ? 'Momentum Score Trend' : 'Avg Momentum Score Trend',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Momentum Score Trend' : 'Avg Momentum Score Trend'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `Analyzes recent changes in this student's Momentum Score to reveal their performance trajectory for this standard.`
                : `The average performance trajectory across all students for this standard.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgTrend',
      sortAttribute: 'avgTrendForSort',
      isSortable: true,
      rowSectionClassName: 'momentum-score-trend'
    },
    {
      width: scaledWidths['175w-100min'],
      ariaLabel: isStudentSpecific ? 'Exposure' : 'Avg Exposure',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Exposure' : 'Avg Exposure'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `A measure of the practice this student has completed for this standard. Higher exposure increases the reliability of the Momentum Score.`
                : `The average amount of practice students have completed for this standard. Higher average exposure increases the reliability of the average Momentum Score.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgExposure',
      sortAttribute: 'avgExposureForSort',
      isSortable: true
    },
    {
      width: scaledWidths['175w-100min'],
      ariaLabel: isStudentSpecific ? 'Questions Answered' : 'Avg Questions Answered',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Questions Answered' : 'Avg Questions Answered'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `The total number of questions this student has answered for this standard.`
                : `The average number of questions answered by students for this standard.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgQuestionsAnswered',
      sortAttribute: 'avgQuestionsAnsweredForSort',
      isSortable: true
    },
    {
      width: scaledWidths['175w-100min'],
      ariaLabel: isStudentSpecific ? 'Replay Rate' : 'Avg Replay Rate',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Replay Rate' : 'Avg Replay Rate'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `Measures the frequency with which this student revisits questions for this standard. A high rate can signify both persistence and potential areas of difficulty.`
                : `The average frequency with which students revisit questions for this standard. A high rate can signify both persistence and potential areas of difficulty across the class.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgReplayRate',
      sortAttribute: 'avgReplayRateForSort',
      isSortable: true
    },
    {
      width: scaledWidths['175w-100min'],
      ariaLabel: isStudentSpecific ? 'Time Spent' : 'Avg Time Spent',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>{isStudentSpecific ? 'Time Spent' : 'Avg Time Spent'}</span>
          <Tooltip
            skipPositioning={true}
            tooltipText={
              isStudentSpecific
                ? `The total time this student has spent answering questions for this standard.`
                : `The average time students have spent answering questions for this standard.`
            }
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'avgTimeSpent',
      sortAttribute: 'avgTimeSpentForSort',
      isSortable: true
    },
    ...(isStudentSpecific ? [{
      width: scaledWidths['200w-110min'],
      ariaLabel: 'Last Practiced',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Last Practiced</span>
          <Tooltip
            skipPositioning={true}
            tooltipText="The most recent date this student answered a question for this standard."
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'lastPracticed',
      sortAttribute: 'lastPracticedForSort',
      isSortable: true
    }] : [{
      width: scaledWidths['200w-110min'],
      ariaLabel: 'Number of Students Practiced',
      name: (
        <span className="text-and-tooltip-wrapper">
          <span>Number of Students Practiced</span>
          <Tooltip
            skipPositioning={true}
            tooltipText="The total number of students who have answered at least one question for this standard, shown as a raw number and a percentage of the class."
            tooltipTriggerText={helpIconImg}
          />
        </span>
      ),
      attribute: 'numberOfStudents',
      sortAttribute: 'numberOfStudentsForSort',
      isSortable: true
    }])
  ];

  if (!data) { return <Spinner /> }

  if (classroomHasNoStudents(classroom)) {
    return <NoStudentsEmptyState className="by-standard" />
  }

  let filteredStateStandards = data.filter(standard => standard.avg_questions_answered > 1 || standard.grades.find(grade => grade === classroom.grade))

  if (selectedGradeLevel) {
    filteredStateStandards = data.filter(standard => standard.grades.find(grade => grade === selectedGradeLevel))
  }

  if (!filteredStateStandards.length) {
    return (
      <div className='by-standard'>
        <div className="empty-state">
          <h2>
            No standards match your class’s grade. Try browsing other grade levels.
          </h2>
        </div>
      </div>
    )
  }

  const renderSkillChip = (skill: SkillInterface) => {
    const { name, skill_sub_categories, } = skill
    const skillCategory = allSkillCategories.find(cat => cat.name === name);

    const skillsReportUrl = skillCategory
      ? `/teachers/progress_reports/skills_report/classroom/${classroom.id}/smart_groups/by_skill?skill_category_id=${skillCategory.id}`
      : `/teachers/progress_reports/skills_report/classroom/${classroom.id}/smart_groups/by_skill`;

    const tooltipText = skill_sub_categories.length > 0
      ? `This standard is aligned to the following Skill Sub-Categories within the <strong>${name}</strong> Skill Category:<br/><br/><strong>${skill_sub_categories.join(', ')}</strong><hr/>💡 <strong>Hint:</strong> Click this tag to view the Skills Report for this Skill Category`
      : `This standard is aligned to the <strong>${name}</strong> Skill Category.<hr/>💡 <strong>Hint:</strong> Click this tag to view the Skills Report for this Skill Category`;

    const skillTag = (
      <DataTableChip color='green' label={name} link={skillsReportUrl} openInNewTab={true} />
    );

    return (
      <ShrinkWrapChip key={name}>
        <Tooltip
          skipPositioning={true}
          tooltipText={tooltipText}
          tooltipTriggerText={skillTag}
        />
      </ShrinkWrapChip>
    )
  }
  
  const rows = filteredStateStandards.map(row => {
    const {
      name,
      description,
      avg_ema,
      avg_trend,
      avg_exposure,
      avg_questions_answered,
      avg_replay_rate,
      avg_time_spent,
      last_practiced,
      number_of_students,
      total_classroom_students,
      skills,
    } = row
    const smartGroup = getSmartGroup(avg_exposure, avg_ema, avg_questions_answered, momentumScoreGoal)
    return {
      id: name,
      name: <div className="name-and-description-wrapper">
        <b>{name}</b>
        <TruncatedDescription description={description} />
      </div>,
      nameForSort: name,
      skillCategories: <div className="skill-categories-wrapper">{skills?.map(skill => renderSkillChip(skill))}</div>,
      skillCategoriesForSort: skills?.join(', '),
      avgMomentumScore: formatMomentumScore(avg_ema, avg_exposure),
      avgMomentumScoreForSort: getSortableMomentumScore(avg_ema),
      avgTrend: formatTrend(avg_trend, avg_questions_answered),
      avgTrendForSort: getSortableTrend(avg_trend),
      avgExposure: avg_questions_answered === 0 ? NONE : avg_exposure,
      avgExposureForSort: getSortableExposure(avg_questions_answered === 0 ? NONE : avg_exposure),
      avgQuestionsAnswered: avg_questions_answered || '—',
      avgQuestionsAnsweredForSort: avg_questions_answered || 0,
      avgReplayRate: formatPercentage(avg_replay_rate, avg_questions_answered),
      avgReplayRateForSort: getSortablePercentage(avg_replay_rate, avg_questions_answered),
      avgTimeSpent: formatTime(avg_time_spent),
      avgTimeSpentForSort: getSortableTime(avg_time_spent),
      lastPracticed: formatLastPracticed(last_practiced),
      lastPracticedForSort: getSortableLastPracticed(last_practiced),
      numberOfStudents: formatStudentCount(number_of_students, total_classroom_students),
      numberOfStudentsForSort: number_of_students || 0,
      smartGroup: <span className={smartGroupToClassName[smartGroup]}>{smartGroup}</span>,
      smartGroupForSort: getSortableSmartGroup(smartGroup, avg_ema, name)
    }
  });

  return (
    <div className="by-standard">
      <div className="data-table-wrapper" ref={wrapperRef}>
        <DataTable
          defaultSortAttribute='smartGroupForSort'
          headers={headers}
          reportingFormat={true}
          rows={rows}
          showCheckboxes={false}
          tableRef={tableRef}
        />
      </div>
      {selectedGradeLevel === null && (
        // max-width (not width): a fixed width here makes the footer the container's
        // widest rigid child on narrow viewports, so the container tracks the table's
        // width and useScaledTableColumnWidths re-measures its own output — a feedback
        // loop that shrinks the table 3px per render until the tab locks up.
        <section className="unpracticed-section-wrapper" style={{ maxWidth: `${headers.reduce((sum, h) => sum + parseFloat(h.width), 0)}px` }}>
          <div className="unpracticed-section">
            <h2>Showing practiced standards from all grades, plus unpracticed standards from {formatGradeLevelWithContext(classroom.grade)}.</h2>
            <p>To view other standards, try selecting a specific Grade Level above.</p>
          </div>
        </section>
      )}
    </div>
  );
}

export default ByStandard
